import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as reviewsApi from '@/api/reviews.api';

/**
 * Query keys for reviews
 */
export const reviewKeys = {
  all: ['reviews'] as const,
  lists: () => [...reviewKeys.all, 'list'] as const,
  list: (params: reviewsApi.ReviewQueryParams) => [...reviewKeys.lists(), params] as const,
  stats: (params?: { userId?: string; repoId?: string; projectId?: string; days?: number }) =>
    [...reviewKeys.all, 'stats', params] as const,
  details: () => [...reviewKeys.all, 'detail'] as const,
  detail: (id: string) => [...reviewKeys.details(), id] as const,
  byCommit: (sha: string) => [...reviewKeys.all, 'commit', sha] as const,
};

/**
 * Fetch reviews with filters
 */
export function useReviews(params?: reviewsApi.ReviewQueryParams) {
  return useQuery({
    queryKey: reviewKeys.list(params || {}),
    queryFn: () => reviewsApi.getReviews(params),
  });
}

/**
 * Fetch review statistics
 */
export function useReviewStats(params?: {
  userId?: string;
  repoId?: string;
  projectId?: string;
  days?: number;
}) {
  return useQuery({
    queryKey: reviewKeys.stats(params),
    queryFn: () => reviewsApi.getReviewStats(params),
  });
}

/**
 * Fetch a single review
 */
export function useReview(id: string) {
  return useQuery({
    queryKey: reviewKeys.detail(id),
    queryFn: () => reviewsApi.getReview(id),
    enabled: !!id,
  });
}

/**
 * Fetch review by commit SHA
 */
export function useReviewByCommit(sha: string) {
  return useQuery({
    queryKey: reviewKeys.byCommit(sha),
    queryFn: () => reviewsApi.getReviewByCommit(sha),
    enabled: !!sha,
  });
}

/**
 * Trigger manual review mutation
 */
export function useTriggerReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commitSha, repoId }: { commitSha: string; repoId: string }) =>
      reviewsApi.triggerReview(commitSha, repoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}
